//src/hooks/useActionPlanModels.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createActionPlanModel,
  createActionPlanModelItem,
  getActionPlanModel,
  listActionPlanModels,
  setActionPlanModelItemStatus,
  setActionPlanModelStatus,
  updateActionPlanModel,
  updateActionPlanModelItem,
} from "@/services/actionPlanModels";
import type {
  ActionPlanModelCreateIn,
  ActionPlanModelItemCreateIn,
  ActionPlanModelItemUpdateIn,
  ActionPlanModelUpdateIn,
} from "@/types/actionPlanModels";

export const ACTION_PLAN_MODELS_QK = (params: { q?: string; is_active?: boolean }) => ["action_plan_models", params] as const;

export function useActionPlanModels(params: { q?: string; is_active?: boolean }) {
  return useQuery({
    queryKey: ACTION_PLAN_MODELS_QK(params),
    queryFn: () => listActionPlanModels(params),
    staleTime: 20_000,
  });
}

export function useActionPlanModelDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["action_plan_model_detail", id],
    queryFn: () => getActionPlanModel(id as string),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function useCreateActionPlanModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActionPlanModelCreateIn) => createActionPlanModel(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action_plan_models"] }),
  });
}

export function useUpdateActionPlanModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ActionPlanModelUpdateIn }) => updateActionPlanModel(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["action_plan_models"] });
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", vars.id] });
    },
  });
}

export function useSetActionPlanModelStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => setActionPlanModelStatus(id, is_active),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["action_plan_models"] });
      qc.invalidateQueries({ queryKey: ["action_plan_model_detail", vars.id] });
    },
  });
}

export function useCreateActionPlanModelItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ modelId, body }: { modelId: string; body: ActionPlanModelItemCreateIn }) => createActionPlanModelItem(modelId, body),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["action_plan_model_detail", vars.modelId] }),
  });
}

export function useUpdateActionPlanModelItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: ActionPlanModelItemUpdateIn }) => updateActionPlanModelItem(itemId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action_plan_model_detail"] }),
  });
}

export function useSetActionPlanModelItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, is_active }: { itemId: string; is_active: boolean }) => setActionPlanModelItemStatus(itemId, is_active),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["action_plan_model_detail"] }),
  });
}
